import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiArrowLeft, FiCheck, FiCopy, FiEdit2, FiImage, FiLoader, FiMessageCircle,
  FiMoreVertical, FiSearch, FiSend, FiTrash2, FiX,
} from 'react-icons/fi';
import {
  cConversations, cDeleteMessage, cDeleteMessageForMe, cEditMessage,
  cSendMessage, cThread, cUpload, fullUrl,
} from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import UserAvatar from '../../components/community/UserAvatar';
import AudioPlayer from '../../components/community/AudioPlayer';
import VoiceRecorder from '../../components/community/VoiceRecorder';
import { BLUE, timeAgo } from '../../components/community/brand';

/** Community chat: conversations list + live thread (polled every 5s). */
export default function MessagesPage() {
  const { userId } = useParams();
  const { t, lang } = useLanguage();
  const { user, requireAuth, refresh } = useCommunity();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [menuFor, setMenuFor] = useState(null);      // message id with the open menu
  const [chatMenu, setChatMenu] = useState(null);    // conversation id with the open menu
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [pending, setPending] = useState([]);        // uploaded pictures waiting to send
  const [uploading, setUploading] = useState(false);
  const bottom = useRef(null);
  const fileRef = useRef(null);

  const loadConversations = () => {
    cConversations().then((rows) => setConversations(Array.isArray(rows) ? rows : [])).catch(() => {});
  };

  useEffect(() => {
    if (!requireAuth(t('loginRequiredDesc'))) { setLoading(false); return; }
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !userId) { setThread(null); return undefined; }
    let alive = true;
    const load = () => {
      cThread(userId)
        .then((res) => {
          if (!alive) return;
          setThread(res);
          setLoading(false);
          refresh();
        })
        .catch(() => { if (alive) setLoading(false); });
    };
    setLoading(true);
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [userId, user, refresh]);

  useEffect(() => {
    if (bottom.current) bottom.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if ((!body && pending.length === 0) || !userId) return;
    setBusy(true);
    try {
      await cSendMessage({
        to_user_id: Number(userId),
        text: body,
        images: pending,                 // one or many pictures
      });
      setText('');
      setPending([]);
      const res = await cThread(userId);
      setThread(res);
      loadConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  /** Save an edited message (sender only — the backend checks that too). */
  const saveEdit = async (id) => {
    const body = editText.trim();
    if (!body) { toast.error(t('writeSomething')); return; }
    setBusy(true);
    try {
      const updated = await cEditMessage(id, { text: body });
      setThread((prev) => (prev
        ? { ...prev, messages: prev.messages.map((m) => (m.id === id ? { ...m, ...updated } : m)) }
        : prev));
      setEditingId(null);
      toast.success(t('updatedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const drop = (id) => {
    setThread((prev) => (prev ? { ...prev, messages: prev.messages.filter((m) => m.id !== id) } : prev));
  };

  /** Delete for me / for everyone. */
  const removeMessage = async (id, forEveryone) => {
    try {
      if (forEveryone) await cDeleteMessage(id);
      else await cDeleteMessageForMe(id);
      drop(id);
      toast.success(t('deletedOk'));
      loadConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const copyMessage = async (body) => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success(t('linkCopied'));
    } catch (e) {
      toast(body);
    }
  };

  /** Remove a whole conversation for me (every message, one by one). */
  const deleteChat = async (otherId) => {
    if (!window.confirm(t('deleteChatConfirm'))) return;
    setChatMenu(null);
    try {
      const res = await cThread(otherId);
      await Promise.all((res.messages || []).map((m) => cDeleteMessageForMe(m.id).catch(() => {})));
      toast.success(t('deletedOk'));
      loadConversations();
      if (String(otherId) === String(userId)) navigate('/messages');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  /** Send a recorded voice note (already uploaded + compressed to MP3). */
  const sendVoice = async ({ url }) => {
    if (!userId) return;
    try {
      await cSendMessage({ to_user_id: Number(userId), audio: url });
      const res = await cThread(userId);
      setThread(res);
      loadConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  /** Upload the chosen pictures (one or many) and stage them for sending. */
  const attachMany = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0 || !userId) return;
    setUploading(true);
    try {
      const up = await cUpload(list);
      const urls = (up.urls || []).filter(Boolean);
      setPending((prev) => [...prev, ...urls].slice(0, 6));
      if (urls.length) toast.success(t('imagesReady'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-bold">{t('loginRequiredTitle')}</p>
        <p className="text-sm text-gray-500 mt-1">{t('loginRequiredDesc')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-3 lg:px-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
        {/* Conversations */}
        <aside className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden ${userId ? 'hidden md:block' : ''}`}>
          <h1 className="px-4 py-3 font-extrabold text-sm border-b border-gray-100 dark:border-gray-800 inline-flex items-center gap-2">
            <FiMessageCircle style={{ color: BLUE }} /> {t('messageNav')}
          </h1>
          <div className="px-3 py-2 border-b border-gray-50 dark:border-gray-800">
            <div className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 px-3 py-1.5">
              <FiSearch className="w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('searchChat')}
                className="flex-1 min-w-0 bg-transparent text-sm outline-none dark:text-gray-200"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} aria-label={t('close')}>
                  <FiX className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>
          {(conversations.filter((c) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            const u = c.user || {};
            return `${u.name || ''} ${u.username || ''} ${c.last_message || ''}`.toLowerCase().includes(q);
          })).length === 0 ? (
            <p className="p-4 text-sm text-gray-400">{t('noConversations')}</p>
          ) : (
            conversations
              .filter((c) => {
                const q = search.trim().toLowerCase();
                if (!q) return true;
                const u = c.user || {};
                return `${u.name || ''} ${u.username || ''} ${c.last_message || ''}`.toLowerCase().includes(q);
              })
              .map((c) => (
              <div key={c.user?.id || c.last_at} className="relative group/conv">
                <button
                  type="button"
                  onClick={() => navigate(`/messages/${c.user?.id}`)}
                  className={`w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-50 dark:border-gray-800 ${
                    String(c.user?.id) === String(userId) ? 'bg-gray-50 dark:bg-gray-800' : ''
                  }`}
                >
                  <UserAvatar user={c.user} size={42} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{c.user?.name || c.user?.username}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {c.last_from_me ? `${t('sendMessage')}: ` : ''}{c.last_message}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 pr-5">
                    <span className="text-[10px] text-gray-400">{timeAgo(c.last_at, lang)}</span>
                    {c.unread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{ background: BLUE }}>
                        {c.unread}
                      </span>
                    )}
                  </div>
                </button>

                {/* per-conversation menu */}
                <button
                  type="button"
                  onClick={() => setChatMenu((v) => (v === c.user?.id ? null : c.user?.id))}
                  className="absolute top-2 right-1.5 p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover/conv:opacity-100 focus:opacity-100"
                  aria-label={t('messageOptions')}
                >
                  <FiMoreVertical className="w-4 h-4" />
                </button>
                {chatMenu === c.user?.id && (
                  <div className="absolute right-1.5 top-8 z-30 w-44 rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-1.5 anim-pop">
                    <button
                      type="button"
                      onClick={() => deleteChat(c.user?.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
                    >
                      <FiTrash2 className="w-4 h-4" /> {t('deleteChat')}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </aside>

        {/* Thread */}
        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 flex flex-col min-h-[60vh] max-h-[78vh] overflow-hidden">
          {!userId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <img src={`${process.env.PUBLIC_URL}/ckl.png`} alt="CodeKhmerLearning" className="w-14 h-14 opacity-90" />
              <p className="mt-3 text-sm text-gray-500">{t('noConversations')}</p>
            </div>
          ) : (
            <>
              <header className="flex items-center gap-3 px-3.5 py-3 border-b border-gray-100 dark:border-gray-800">
                <Link to="/messages" className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Back">
                  <FiArrowLeft className="w-5 h-5" />
                </Link>
                <UserAvatar user={thread?.user} size={38} to={thread?.user ? `/u/${thread.user.username}` : null} />
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{thread?.user?.name || thread?.user?.username}</p>
                  <p className="text-[11px] text-gray-400">@{thread?.user?.username}</p>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 dark:bg-gray-950/40">
                {loading && !thread ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                    <FiLoader className="w-4 h-4 animate-spin" /> {t('loading')}
                  </div>
                ) : (
                  (thread?.messages || []).map((m) => {
                    const mine = m.sender_id === user.id;
                    const editing = editingId === m.id;
                    return (
                      <div key={m.id} className={`group/msg flex items-end gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
                        {!mine && <UserAvatar user={m.sender || thread?.user} size={26} />}
                        <div
                          className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${mine ? 'text-white' : 'bg-white dark:bg-gray-800 dark:text-gray-100'}`}
                          style={mine ? { background: BLUE } : undefined}
                        >
                          {editing ? (
                            <div className="space-y-2 min-w-[200px]">
                              <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                rows={2}
                                autoFocus
                                className="w-full rounded-xl px-2.5 py-2 text-sm text-gray-900 outline-none"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <button type="button" onClick={() => setEditingId(null)} className="text-[11px] font-bold text-white/80">
                                  {t('cancel')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveEdit(m.id)}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-white text-gray-900 disabled:opacity-60"
                                >
                                  <FiCheck className="w-3.5 h-3.5" /> {t('update')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {(() => {
                                const pics = (m.images && m.images.length) ? m.images : (m.image ? [m.image] : []);
                                if (pics.length === 0) return null;
                                return (
                                  <span className={`grid gap-1 mb-1.5 ${pics.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                    {pics.map((u) => (
                                      <a key={u} href={fullUrl(u)} target="_blank" rel="noreferrer" className="block">
                                        <img
                                          src={fullUrl(u)}
                                          alt="attachment"
                                          className="rounded-xl w-full max-h-64 object-cover bg-black/10"
                                        />
                                      </a>
                                    ))}
                                  </span>
                                );
                              })()}
                              {m.audio && (
                                <span className="block mb-1">
                                  <AudioPlayer src={m.audio} compact />
                                </span>
                              )}
                              {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                              <span className={`block text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                                {timeAgo(m.created_at, lang)}
                                {m.edited_at ? ` · ${t('edited')}` : ''}
                              </span>
                            </>
                          )}
                        </div>

                        {/* per-message actions: edit / copy / delete */}
                        {!editing && (
                          <div className="relative shrink-0">
                            <button
                              type="button"
                              onClick={() => setMenuFor((v) => (v === m.id ? null : m.id))}
                              className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover/msg:opacity-100 focus:opacity-100"
                              aria-label={t('messageOptions')}
                            >
                              <FiMoreVertical className="w-4 h-4" />
                            </button>
                            {menuFor === m.id && (
                              <div className={`absolute bottom-8 z-30 w-48 rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-1.5 anim-pop ${mine ? 'right-0' : 'left-0'}`}>
                                {mine && (
                                  <button
                                    type="button"
                                    onClick={() => { setMenuFor(null); setEditingId(m.id); setEditText(m.text || ''); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                                  >
                                    <FiEdit2 className="w-4 h-4" /> {t('editMessage')}
                                  </button>
                                )}
                                {m.text && (
                                  <button
                                    type="button"
                                    onClick={() => { setMenuFor(null); copyMessage(m.text); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                                  >
                                    <FiCopy className="w-4 h-4" /> {t('copyText')}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => { setMenuFor(null); removeMessage(m.id, false); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
                                >
                                  <FiTrash2 className="w-4 h-4" /> {t('deleteForMe')}
                                </button>
                                {mine && (
                                  <button
                                    type="button"
                                    onClick={() => { setMenuFor(null); removeMessage(m.id, true); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
                                  >
                                    <FiTrash2 className="w-4 h-4" /> {t('deleteForEveryone')}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={bottom} />
              </div>

              {/* pictures staged for the next message */}
              {pending.length > 0 && (
                <div className="flex items-center gap-2 px-3 pt-3 flex-wrap border-t border-gray-100 dark:border-gray-800">
                  {pending.map((u) => (
                    <span key={u} className="relative">
                      <img src={fullUrl(u)} alt="to send" className="w-16 h-16 rounded-xl object-cover" />
                      <button
                        type="button"
                        onClick={() => setPending((prev) => prev.filter((x) => x !== u))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow"
                        aria-label={t('remove')}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  <span className="text-[11px] font-semibold text-gray-400">
                    {pending.length}/6 · {t('pressSend')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPending([])}
                    className="ml-auto text-[11px] font-bold text-gray-400 hover:text-red-500"
                  >
                    {t('clearAll')}
                  </button>
                </div>
              )}

              <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => fileRef.current && fileRef.current.click()}
                  disabled={uploading || pending.length >= 6}
                  className="p-2.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  aria-label={t('uploadImages')}
                  title={t('uploadImages')}
                >
                  {uploading ? <FiLoader className="w-5 h-5 animate-spin" /> : <FiImage className="w-5 h-5" />}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => attachMany(e.target.files)}
                />
                <VoiceRecorder kind="voice" compact onUploaded={sendVoice} />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t('typeMessage')}
                  className="flex-1 rounded-full border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-4 py-2.5 text-sm outline-none focus:border-gray-400"
                />
                <button
                  type="submit"
                  data-sfx="send"
                  disabled={busy || uploading || (!text.trim() && pending.length === 0)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-white text-sm font-bold disabled:opacity-50"
                  style={{ background: BLUE }}
                  aria-label={t('sendMessage')}
                >
                  {busy ? <FiLoader className="w-5 h-5 animate-spin" /> : <FiSend className="w-5 h-5" />}
                  <span className="hidden sm:inline">{t('sendMessage')}</span>
                  {pending.length > 0 && <span className="text-[11px] font-extrabold">+{pending.length}🖼️</span>}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
